/**
 * RelatorioAuditoriaPage — relatório da auditoria (F10).
 *
 * Réplica do audit_report.html (2.132 linhas): resumo por nível de risco,
 * documentos analisados, decretos aplicados e cada apontamento com base
 * legal, jurisprudência, risco e recomendação.
 */

import {
  faArrowLeft,
  faBalanceScale,
  faBook,
  faCalendar,
  faCircleInfo,
  faExclamationTriangle,
  faFileLines,
  faFileWord,
  faGavel,
  faLightbulb,
  faMicroscope,
  faScaleBalanced,
  faSearchDollar,
  faSpinner,
  faTrashAlt,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  type AnaliseForense,
  excluirAuditoria,
  type InformacoesGerais,
  relatorioAuditoria,
  type Apontamento,
} from '../../api/auditoria'
import { useToast } from '../../components/Toast'

const CORES_RISCO: Record<string, string> = {
  'Crítico': 'border-red-500 bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300',
  Alto: 'border-orange-500 bg-orange-50 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  'Médio': 'border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
  Baixo: 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300',
}

// sem acento: relatórios antigos gravaram "CRITICO"/"MEDIO", que caíam na
// cor de Baixo (verde) por não casar com "Crít"/"Médi"
const semAcento = (texto: string) =>
  texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

function corDoRisco(risco: string) {
  const chave = Object.keys(CORES_RISCO).find((n) =>
    semAcento(risco).startsWith(semAcento(n).slice(0, 4)),
  )
  return CORES_RISCO[chave ?? 'Baixo']
}

function CardApontamento({ apontamento }: { apontamento: Apontamento }) {
  const [aberto, setAberto] = useState(false)
  const campos: [string, string, typeof faBook][] = [
    ['Descrição', apontamento.descricao_html, faFileLines],
    ['Base Legal', apontamento.base_legal_html, faGavel],
    ['Fundamentação Jurisprudencial', apontamento.jurisprudencia_html, faBalanceScale],
    ['Justificativa do Risco', apontamento.justificativa_risco_html, faTriangleExclamation],
    ['Precedentes Aplicáveis', apontamento.precedentes_html, faBook],
    ['Recomendação', apontamento.recomendacao_html, faLightbulb],
  ]

  return (
    <li className={`overflow-hidden rounded-xl border-l-4 ${corDoRisco(apontamento.risco)}`}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="flex w-full items-start justify-between gap-3 p-4 text-left"
      >
        <span className="min-w-0">
          <span className="block font-semibold">{apontamento.titulo}</span>
          {apontamento.item_verificado && (
            <span className="mt-1 block text-sm opacity-80">
              {apontamento.item_verificado}
            </span>
          )}
          {apontamento.documento && (
            <span className="mt-1 block text-xs opacity-70">
              {apontamento.documento}
            </span>
          )}
        </span>
        <span className="shrink-0 rounded-full bg-white/70 px-3 py-1 text-xs font-bold dark:bg-black/20">
          {apontamento.risco}
        </span>
      </button>
      {aberto && (
        <div className="space-y-4 border-t border-white/50 bg-white p-4 dark:bg-dark-card">
          {campos
            .filter(([, html]) => html)
            .map(([rotulo, html, icone]) => (
              <div key={rotulo}>
                <p className="mb-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  <FontAwesomeIcon icon={icone} className="mr-2 text-slate-400" />
                  {rotulo}
                </p>
                <div
                  className="conteudo-documento text-sm text-slate-700 dark:text-slate-300"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </div>
            ))}
        </div>
      )}
    </li>
  )
}

/**
 * Análise forense: conteúdo gerado pelo agente (Lei de Benford nos preços,
 * consistência temporal, entidades e vínculos no grafo). Já ia no DOCX —
 * sem ela na tela, o documento exportado diria mais que a visualização.
 */
function SecaoForense({ forense }: { forense: AnaliseForense }) {
  if (!forense.disponivel) return null

  const grave = (nivel: string) => nivel === 'CRÍTICO' || nivel === 'ALTO'
  const entidades = forense.cnpjs.length + forense.empresas.length

  return (
    <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
      <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
        <FontAwesomeIcon icon={faMicroscope} className="text-indigo-500" />
        Análise forense
      </h2>
      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
        Técnicas aplicadas ao documento: Lei de Benford nos preços,
        consistência temporal e vínculos no grafo de conhecimento.
      </p>

      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
          <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">
            Entidades detectadas
          </p>
          <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">
            {entidades}
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400">
            {forense.cnpjs.length} CNPJs · {forense.empresas.length} empresas
          </p>
        </div>
        <div
          className={`rounded-xl border p-4 ${
            forense.grafo_indisponivel
              ? 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40'
              : 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-900/20'
          }`}
        >
          <p className="text-xs font-semibold text-purple-700 dark:text-purple-300">
            Grafo de vínculos
          </p>
          <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">
            {forense.grafo_consultado
              ? 'Consultado'
              : forense.grafo_indisponivel
                ? 'Não consultado'
                : '—'}
          </p>
        </div>
        {(
          [
            ['Análise temporal', forense.temporal],
            ['Lei de Benford (preços)', forense.precos],
          ] as const
        ).map(([rotulo, grupo]) => (
          <div
            key={rotulo}
            className={`rounded-xl border p-4 ${
              grave(grupo.nivel)
                ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                : 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20'
            }`}
          >
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              {rotulo}
            </p>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {grupo.nivel || 'Sem anomalias'}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {grupo.alertas.length} alerta(s)
            </p>
          </div>
        ))}
      </div>

      {(forense.cnpjs.length > 0 || forense.empresas.length > 0) && (
        <details className="mb-3">
          <summary className="cursor-pointer text-sm font-medium text-indigo-600 dark:text-indigo-400">
            Ver entidades detectadas
          </summary>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[...forense.cnpjs, ...forense.empresas].map((e, i) => (
              <span
                key={`${e}-${i}`}
                className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-700 dark:text-slate-200"
              >
                {e}
              </span>
            ))}
          </div>
        </details>
      )}

      {forense.grafo_indisponivel && (
        <p className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
          <FontAwesomeIcon icon={faCircleInfo} className="mr-2 text-slate-400" />
          {forense.grafo_indisponivel} Isto é uma limitação do ambiente, não um
          achado sobre a contratação: nada foi afirmado nem negado a respeito de
          vínculos entre as entidades.
        </p>
      )}

      {forense.contexto_grafo && (
        <details className="mb-3">
          <summary className="cursor-pointer text-sm font-medium text-purple-600 dark:text-purple-400">
            Ver vínculos no grafo (KAG/Neo4j)
          </summary>
          <p className="mt-2 whitespace-pre-line rounded-lg bg-purple-50 p-3 text-sm text-slate-700 dark:bg-purple-900/20 dark:text-slate-300">
            {forense.contexto_grafo}
          </p>
        </details>
      )}

      {(
        [
          ['Alertas temporais', forense.temporal, 'red'],
          ['Alertas de preços (Benford)', forense.precos, 'amber'],
        ] as const
      ).map(([titulo, grupo, cor]) =>
        grupo.alertas.length ? (
          <div
            key={titulo}
            className={`mb-3 rounded-lg border-l-4 p-4 ${
              cor === 'red'
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                : 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
            }`}
          >
            <p className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
              {titulo}
            </p>
            <ul className="space-y-2">
              {grupo.alertas.map((a, i) => (
                <li
                  key={i}
                  className="rounded border border-slate-200 bg-white p-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                >
                  <span className="mr-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-bold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    {a.tipo}
                  </span>
                  <span className="text-slate-700 dark:text-slate-300">
                    {a.descricao}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null,
      )}

      {forense.nivel_risco_geral && (
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Risco consolidado da análise forense:{' '}
          <strong>{forense.nivel_risco_geral}</strong> (score{' '}
          {forense.score_risco.toFixed(1)}).
        </p>
      )}
    </section>
  )
}


/**
 * Cabeçalho do documento auditado (processo, órgão, objeto, modalidade) e o
 * veredito. Vinha só dentro do JSON do relatório: a tela não mostrava nem o
 * número do processo.
 */
function SecaoInformacoes({ info }: { info: InformacoesGerais }) {
  const campos: [string, string][] = [
    ['Processo', info.processo],
    ['Órgão', info.orgao],
    ['Tipo de documento', info.tipo_documento],
    ['Modalidade', info.modalidade],
    ['Valor estimado', info.valor_estimado],
    ['Data do documento', info.data_documento],
    ['Responsável', info.responsavel],
    ['Data da auditoria', info.data_auditoria],
  ].filter(([, v]) => v) as [string, string][]

  if (!campos.length && !info.objeto && !info.status_geral) return null

  const conforme = info.status_geral.toUpperCase().startsWith('CONFORME')

  return (
    <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
      {info.status_geral && (
        <p
          className={`mb-4 rounded-xl border-l-4 p-4 font-bold ${
            conforme
              ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300'
              : 'border-red-500 bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300'
          }`}
        >
          <FontAwesomeIcon
            icon={conforme ? faScaleBalanced : faTriangleExclamation}
            className="mr-2"
          />
          {info.status_geral}
          {info.campos_verificados > 0 && (
            <span className="ml-2 font-normal opacity-80">
              · {info.campos_verificados} campo(s) verificado(s)
            </span>
          )}
        </p>
      )}

      {info.objeto && (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Objeto
          </p>
          <p className="text-slate-700 dark:text-slate-300">{info.objeto}</p>
        </div>
      )}

      {campos.length > 0 && (
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {campos.map(([rotulo, valor]) => (
            <div key={rotulo}>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {rotulo}
              </dt>
              <dd className="text-sm text-slate-700 dark:text-slate-300">{valor}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  )
}


export function RelatorioAuditoriaPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()

  const { data: auditoria, isLoading, error } = useQuery({
    queryKey: ['auditoria-relatorio', idNumerico],
    queryFn: () => relatorioAuditoria(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  const excluir = useMutation({
    mutationFn: () => excluirAuditoria(idNumerico),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['auditorias'] })
      notificar('success', 'Auditoria excluída.')
      navegar('/auditoria')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando relatório…
      </p>
    )
  }
  if (error || !auditoria) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faSearchDollar} className="mb-4 text-5xl text-slate-300" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Auditoria não encontrada
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ?? 'A auditoria não existe ou não é sua.'}
        </p>
        <Link
          to="/auditoria"
          className="inline-flex items-center rounded-lg bg-rose-600 px-6 py-3 font-semibold text-white hover:bg-rose-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 text-center">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faSearchDollar} className="mr-3 text-rose-600" />
          Relatório de Auditoria
        </h1>
        <p className="mb-4 text-lg text-slate-600 dark:text-slate-400">
          {auditoria.arquivo}
        </p>
        {auditoria.criado_em && (
          <span className="inline-flex items-center rounded-full bg-rose-100 px-4 py-2 text-sm font-medium text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">
            <FontAwesomeIcon icon={faCalendar} className="mr-2" />
            {auditoria.criado_em}
          </span>
        )}
      </header>

      <div className="mb-6 flex flex-col justify-center gap-3 sm:flex-row">
        <a
          href={`/audit/report/${auditoria.id}/export/docx`}
          className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-emerald-700"
        >
          <FontAwesomeIcon icon={faFileWord} className="mr-2" />
          Exportar DOCX
        </a>
        <a
          href={`/audit/social-network/${auditoria.id}`}
          className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-indigo-700"
        >
          <FontAwesomeIcon icon={faScaleBalanced} className="mr-2" />
          Rede de relações
        </a>
        <button
          type="button"
          onClick={() => excluir.mutate()}
          disabled={excluir.isPending}
          className="inline-flex items-center justify-center rounded-lg bg-red-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-red-700 disabled:opacity-60"
        >
          <FontAwesomeIcon
            icon={excluir.isPending ? faSpinner : faTrashAlt}
            spin={excluir.isPending}
            className="mr-2"
          />
          Excluir
        </button>
        <Link
          to="/auditoria"
          className="inline-flex items-center justify-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-slate-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>

      {auditoria.erro && (
        <div className="mb-6 rounded-r-xl border-l-4 border-red-400 bg-red-50 p-4 dark:border-red-600 dark:bg-red-900/20">
          <p className="text-red-800 dark:text-red-300">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            {auditoria.erro}
          </p>
        </div>
      )}

      <SecaoInformacoes info={auditoria.informacoes} />

      <section className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        {auditoria.resumo_riscos.map((r) => (
          <div
            key={r.nivel}
            className={`rounded-xl border-l-4 p-4 ${corDoRisco(r.nivel)}`}
          >
            <p className="text-xs uppercase opacity-80">{r.nivel}</p>
            <p className="text-2xl font-bold">{r.total}</p>
          </div>
        ))}
      </section>

      {(auditoria.documentos.length > 0 || auditoria.decretos.length > 0) && (
        <section className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {auditoria.documentos.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-dark-card">
              <h2 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                Documentos analisados
              </h2>
              <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                {auditoria.documentos.map((d, i) => (
                  <li key={i}>
                    • {d.arquivo}
                    {d.tipo && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 text-xs dark:bg-slate-700">
                        {d.tipo}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {auditoria.decretos.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-dark-card">
              <h2 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                Decretos municipais aplicados
              </h2>
              <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                {auditoria.decretos.map((d, i) => (
                  <li key={i}>
                    • Decreto {d.numero}
                    {d.ano && `/${d.ano}`}
                    {d.municipio && ` — ${d.municipio}`}
                    {d.titulo && (
                      <span className="block pl-3 text-xs text-slate-500 dark:text-slate-500">
                        {d.titulo}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <SecaoForense forense={auditoria.forense} />

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2 text-rose-600" />
          Apontamentos ({auditoria.total_apontamentos})
        </h2>
        {auditoria.apontamentos.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
            Nenhum apontamento registrado nesta auditoria.
          </p>
        ) : (
          <ul className="space-y-3">
            {auditoria.apontamentos.map((a, i) => (
              <CardApontamento key={a.id || i} apontamento={a} />
            ))}
          </ul>
        )}
      </section>

      {auditoria.relatorio_html && (
        <details className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
          <summary className="cursor-pointer bg-gradient-to-r from-rose-500 to-amber-500 p-6">
            <span className="text-xl font-bold text-white">
              <FontAwesomeIcon icon={faFileLines} className="mr-3" />
              Relatório completo
            </span>
            <span className="ml-2 text-sm text-white/80">
              (integridade digital, grafo de vínculos e os apontamentos na
              íntegra)
            </span>
          </summary>
          <div className="p-8">
            <div
              className="conteudo-documento leading-relaxed text-slate-700 dark:text-slate-300"
              dangerouslySetInnerHTML={{ __html: auditoria.relatorio_html }}
            />
          </div>
        </details>
      )}
    </div>
  )
}
